import type { Market, Role, Stage } from '@ouradent/shared';

export type Profile = {
  id: string;
  full_name: string;
  email: string;
  role: Role;
  external: boolean;
  avatar_bg: string | null;
  avatar_fg: string | null;
};

export type StepState = 'ok' | 'stale' | 'missing' | 'review' | 'none';

/** A row of the `lead_board` view. */
export type BoardLead = {
  id: string;
  person: string;
  salutation: string | null;
  clinic: string;
  city: string | null;
  market: Market;
  tz: string;
  email: string | null;
  phone_e164: string | null;
  source: string | null;
  campaign: string | null;
  owner_id: string | null;
  stage: Stage;
  dm: boolean | null;
  lost_reason: string | null;
  return_at: string | null;
  created_at: string;
  updated_at: string;
  step_id: string | null;
  step_text: string | null;
  step_due: string | null;
  step_owner: string | null;
  open_tasks: number;
  call_to_review: boolean;
  step_state: StepState;
  website: string | null;
  wants: string | null;
  our_plan: string | null;
  background: string | null;
  form_answers: Record<string, string> | null;
  brought_by: string | null;
  warm: boolean;
};

export type Task = {
  id: string;
  lead_id: string | null;
  call_id: string | null;
  text: string;
  owner_id: string | null;
  /** A second person on the task ("ours"), or null. */
  co_owner_id: string | null;
  due_at: string;
  done_at: string | null;
  origin: string;
  client_id: string | null;
  priority: Priority;
  stage: TaskStage;
  notes: string | null;
  created_by: string | null;
};

export type Priority = 'urgent' | 'high' | 'normal' | 'low';
export type TaskStage = 'todo' | 'doing' | 'waiting' | 'done';

export const PRIORITIES: { id: Priority; label: string; cls: string }[] = [
  { id: 'urgent', label: 'Urgent', cls: 'red' },
  { id: 'high', label: 'High', cls: 'or' },
  { id: 'normal', label: 'Normal', cls: 'gr' },
  { id: 'low', label: 'Low', cls: 'gr' },
];

export const TASK_STAGES: { id: TaskStage; label: string }[] = [
  { id: 'todo', label: 'To do' },
  { id: 'doing', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'done', label: 'Done' },
];

export type Client = { id: string; name: string; colour: string; created_by: string | null };

export type Promise_ = {
  text: string;
  owner: string | null;
  due: string;
  on: boolean;
  /** Quote from the pasted text, set when the promise came from AI extraction. */
  evidence?: string | undefined;
};

export type Call = {
  id: string;
  lead_id: string | null;
  title: string;
  started_at: string;
  duration_min: number | null;
  status: 'review' | 'done' | 'unlinked' | 'dismissed';
  attendee: {
    name?: string;
    email?: string;
    salutation?: string;
    clinic?: string;
    city?: string;
    market?: Market;
  };
  summary: string | null;
  recap_points: string[];
  objections: string[];
  promises: Promise_[];
  next_step: {
    text: string;
    due: string;
    owner: string | null;
    evidence?: string | undefined;
  } | null;
  dm: boolean | null;
  suggested_stage: Stage | null;
  extracted_at: string | null;
  extraction_model: string | null;
  /** Link to the meeting report in Read.ai, for calls that came from Read.ai. */
  report_url: string | null;
  processed_by: string | null;
  processed_at: string | null;
};

export type InboxRow = {
  kind: 'call' | 'over' | 'step';
  priority: number;
  ref_id: string;
  lead_id: string | null;
  title: string;
  at: string | null;
  owner_id: string | null;
};

export type TimelineEvent = {
  id: string;
  lead_id: string;
  kind: string;
  text: string;
  actor_id: string | null;
  at: string;
};
